import ZodiacGlyph from '../horoscope/ZodiacGlyph'
import { ZODIAC_SIGNS } from '../horoscope/zodiacData'
import { ANGLES_12, polarPoint } from '../horoscope/polar'
import { WHEEL_PLANETS } from './wheelPlanetsData'
import { CENTER_ICONS } from './panelsData'

const TICK_ANGLES = Array.from({ length: 72 }, (_, i) => i * 5)
const PLANET_ANGLES = Array.from({ length: 9 }, (_, i) => i * 40)

function PlanetGlyph({ planet, x, y }) {
  return (
    <g transform={`translate(${x},${y})`} stroke="#E6C878" strokeOpacity="0.85" strokeWidth="1.1" strokeLinecap="round" fill="none">
      {planet.circle && <circle cx={planet.circle.cx} cy={planet.circle.cy} r={planet.circle.r} />}
      {planet.paths?.map((d, i) => (
        <path key={`p${i}`} d={d} />
      ))}
      {planet.lines?.map(([x1, y1, x2, y2], i) => (
        <line key={`l${i}`} x1={x1} y1={y1} x2={x2} y2={y2} />
      ))}
      {planet.polylines?.map((pts, i) => (
        <polyline key={`pl${i}`} points={pts.join(',')} />
      ))}
    </g>
  )
}

function OuterZodiacRing() {
  return (
    <div className="absolute inset-0 animate-[spin_150s_linear_infinite] motion-reduce:animate-none">
      <svg viewBox="0 0 400 400" className="h-full w-full" fill="none">
        <circle cx="200" cy="200" r="198" stroke="#E6C878" strokeOpacity="0.45" />
        <circle cx="200" cy="200" r="170" stroke="#C99A2E" strokeOpacity="0.28" />
        {TICK_ANGLES.map((deg) => {
          const isLong = deg % 15 === 0
          return (
            <line
              key={deg}
              x1="200"
              y1="2"
              x2="200"
              y2={isLong ? '16' : '9'}
              stroke="#E6C878"
              strokeOpacity={isLong ? '0.5' : '0.22'}
              transform={`rotate(${deg} 200 200)`}
            />
          )
        })}
        <g fontFamily="'Noto Sans Symbols','Segoe UI Symbol','Apple Symbols',serif" fontWeight="600">
          {ANGLES_12.map((deg, i) => {
            const p = polarPoint(200, 200, 183, deg)
            return <ZodiacGlyph key={deg} sign={ZODIAC_SIGNS[i].sign} x={p.x - 9} y={p.y - 9} width="18" height="18" style={{ color: 'rgba(244,222,166,0.9)' }} />
          })}
        </g>
      </svg>
    </div>
  )
}

function MiddlePlanetRing() {
  return (
    <div className="absolute inset-[9%] animate-[spin_100s_linear_infinite_reverse] motion-reduce:animate-none">
      <svg viewBox="0 0 400 400" className="h-full w-full" fill="none">
        <circle cx="200" cy="200" r="176" stroke="#E8770F" strokeOpacity="0.32" />
        {ANGLES_12.map((deg) => (
          <ellipse key={deg} cx="200" cy="72" rx="16" ry="40" stroke="#E6C878" strokeOpacity="0.18" transform={`rotate(${deg} 200 200)`} />
        ))}
        <g>
          {PLANET_ANGLES.map((deg, i) => {
            const p = polarPoint(200, 200, 150, deg)
            return <PlanetGlyph key={deg} planet={WHEEL_PLANETS[i]} x={p.x} y={p.y} />
          })}
        </g>
      </svg>
    </div>
  )
}

function InnerDashedRing() {
  return (
    <div className="absolute inset-[30%] animate-[spin_64s_linear_infinite] motion-reduce:animate-none">
      <svg viewBox="0 0 200 200" className="h-full w-full" fill="none">
        <circle cx="100" cy="100" r="96" stroke="#E6C878" strokeOpacity="0.26" strokeDasharray="2 9" />
      </svg>
    </div>
  )
}

function CenterJewel({ currentIndex }) {
  const icon = CENTER_ICONS[currentIndex]
  return (
    <div className="absolute inset-[34%] grid place-items-center rounded-full bg-[radial-gradient(circle,rgba(26,14,6,0.95),rgba(12,7,3,0.6)_75%,transparent)] ring-1 ring-[#E6C878]/35 shadow-[0_0_60px_-8px_rgba(232,119,15,0.6)]">
      <div className="text-[#F4DEA6]" style={{ filter: 'drop-shadow(rgba(232,119,15,0.7) 0px 0px 16px)' }}>
        <svg className="h-12 w-12 md:h-16 md:w-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
          {icon.circle && <circle cx={icon.circle.cx} cy={icon.circle.cy} r={icon.circle.r} />}
          {icon.circles?.map((c, i) => (
            <circle key={i} cx={c.cx} cy={c.cy} r={c.r} />
          ))}
          {icon.paths?.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </svg>
      </div>
    </div>
  )
}

export default function WhyChooseZodiacWheel({ currentIndex = 0, mobile = false }) {
  const wrapperClass = mobile
    ? 'pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.14] lg:hidden'
    : 'hidden items-center justify-center lg:flex'

  return (
    <div className={wrapperClass} aria-hidden="true">
      <div className={mobile ? 'w-[150vw] max-w-140' : 'relative aspect-square w-full max-w-130 select-none'}>
        <div className={mobile ? 'relative aspect-square w-full max-w-130 select-none' : ''}>
          {!mobile && <div className="absolute inset-[12%] rounded-full bg-[radial-gradient(circle,rgba(232,119,15,0.30),transparent_70%)] blur-3xl" />}
          <OuterZodiacRing />
          {!mobile && (
            <>
              <MiddlePlanetRing />
              <InnerDashedRing />
              <CenterJewel currentIndex={currentIndex} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
